/**
 * FROZEN CONTRACT — the versioned root Save object. "The save IS the world."
 *
 * Persisted to IndexedDB (store/). Export/import-to-file lets the owner back it up.
 * Every layer reads/writes through this shape; bump `SCHEMA_VERSION` + add a
 * migration whenever it changes.
 */
import type { PokemonInstance } from './instance.js';
import type { SimTrainer } from './society.js';
import type { CodexEntry } from './lore.js';
import type { QuestState } from './quest.js';

/** Current on-disk schema version. Bump on any breaking shape change. */
export const SCHEMA_VERSION = 7;

/** Difficulty dial (CLAUDE.md): Cozy → Spicy, gentle rubber-banding within. */
export type Difficulty = 'cozy' | 'mild' | 'spicy';

/**
 * The player's own avatar. Both the player AND the simulated residents are now REAL
 * Pokemon-game TRAINERS (Pokemon Showdown trainer sprites), not chibis/Pokemon. The
 * `trainerKey` is the vendored sprite stem (e.g. 'red', 'leaf', 'acetrainerf'); the
 * renderer (scenes/sprites/trainerAvatar) resolves it to a texture with a graceful
 * placeholder fallback if the sprite is missing.
 */
export interface PlayerAvatar {
  /** The chosen Showdown trainer sprite key (vendored to /generated/trainers/<key>.png). */
  readonly trainerKey: string;
}

/** Player-owned settings, including the cost/voice dials. */
export interface Settings {
  readonly difficulty: Difficulty;
  /** Master LLM toggle; off ⇒ template floor only. */
  readonly llmEnabled: boolean;
  /** Client-side mirror of the daily token budget (server enforces the real cap). */
  readonly dailyTokenBudget: number;
  /** Reduce motion / animations for calm. */
  readonly reducedMotion: boolean;
  /** UI/text scale for touch + accessibility. */
  readonly uiScale: number;
}

/** The player's own profile. */
export interface PlayerProfile {
  readonly id: 'player';
  /**
   * Stable per-save identity (D16: multi-slot saves). Keys the save in IndexedDB
   * and on the server. Minted once at New Game (e.g. `slot-<createdAt>`) and never
   * reused — New Game always creates a NEW slot, never overwriting another.
   */
  readonly slotId: string;
  name: string;
  /** Chosen starter species id. */
  readonly starterSpeciesId: string;
  /** The player's chosen Showdown trainer avatar (a `trainerKey`). */
  readonly avatar: PlayerAvatar;
  /**
   * Species id of the team Pokemon that walks BEHIND the player in the overworld
   * (a chosen team member follows the human trainer). Null ⇒ no follower shown.
   * Defaults to the first team member (the starter) on a new game.
   */
  followerSpeciesId: string | null;
  /** Real wall-clock ms the save was created. */
  readonly createdAt: number;
}

/** Per-species dex progress. */
export interface DexState {
  /** Species ids the player has seen. */
  readonly seen: readonly string[];
  /** Species ids the player has caught (the living dex). */
  readonly caught: readonly string[];
}

/** The hometown ranch/camp state (berries, day-camp, eggs). */
export interface RanchState {
  /** Berry plots: slot index → { berryId, plantedAt }. */
  readonly berryPlots: readonly { readonly berryId: string; readonly plantedAt: number }[];
  /** Instance ids of mons benched in the day-camp (slowly level + gain friendship). */
  readonly campMemberIds: readonly string[];
  /** Eggs in the hatchery: steps walked toward hatching. */
  readonly eggs: readonly {
    readonly id: string;
    readonly speciesId: string;
    readonly stepsRemaining: number;
  }[];
}

/** Persisted society state (the brain of the simulated MMO). */
export interface SocietyState {
  readonly trainers: readonly SimTrainer[];
  /** Guild ids the player belongs to. */
  readonly playerGuildIds: readonly string[];
}

/** Persisted lore/codex unlock state. */
export interface LoreState {
  /** Unlocked codex entries (authored seeds + grown threads). */
  readonly entries: readonly CodexEntry[];
  /** Ids of entries the player has actually read (for "new" badges, never nags). */
  readonly readEntryIds: readonly string[];
}

// ── Round-3 arc blocks (v7; notes/r3-design/) ─────────────────────────────────────
// All four are OPTIONAL on Save — a v6 save upgrades losslessly and every reader
// treats "absent" as "empty". None of them can ever punish (triple contract):
// fullness rests at a floor, league losses cost nothing, the rival never gets ahead.

/** Follower-care side-table row, keyed by the mon's stable instance id (happiness is
 *  NOT stored here — it IS `instance.friendship`, already persisted). */
export interface FollowerCareRecord {
  readonly instanceId: string;
  /** 0..100; eases toward a comfortable floor with absence, never below it. */
  fullness: number;
  /** Wall-clock ms of the last care interaction (drives gentle overnight easing). */
  lastCaredAt: number;
  /** A granted follower cosmetic, if any. */
  cosmeticId: string | null;
}
export interface CareState {
  readonly records: readonly FollowerCareRecord[];
}
export function emptyCareState(): CareState {
  return { records: [] };
}

/** One Hall of Fame entry (a Champion clear's team snapshot). */
export interface HallOfFameRecord {
  readonly clearedAt: number; // wall-clock ms
  readonly inGameDate: string;
  readonly team: readonly {
    readonly speciesId: string;
    readonly nickname: string | null;
    readonly level: number;
  }[];
}
/** Elite Four / Champion progress. Losses never regress boutIndex (no gauntlet stress). */
export interface LeagueState {
  /** 0..5 — the next bout to face; 5 == cleared this run. */
  boutIndex: number;
  /** A run is underway (exit-anytime, resume-anytime). */
  inProgress: boolean;
  /** Total Champion clears (rematch counter). */
  clears: number;
  readonly hallOfFame: readonly HallOfFameRecord[];
}
export function emptyLeagueState(): LeagueState {
  return { boutIndex: 0, inProgress: false, clears: 0, hallOfFame: [] };
}

/** The pinned warm rival (null until the first meeting resolves one). */
export interface RivalState {
  readonly trainerId: string; // the pinned SimTrainer id
  readonly starterSpeciesId: string; // their mirrored starter (base species)
  /** Kept at max(0, playerBadges - 1) — always just behind, never ahead-of-you stress. */
  badges: number;
  sparWins: number; // shared history, never shaming
  sparLosses: number; // framed warmly, never a penalty
  lastGreetedBadge: number; // last badge tier we met at (anteroom callback logic)
  readonly firstMetAt: number; // wall-clock ms — "we've been at this together" flavor
}

/** Player position in the walkable overworld (null ⇒ spawn at the area default). */
export interface OverworldPos {
  readonly area: string; // 'Town' | a node id ('node-meadow', …)
  x: number;
  y: number;
  facing: 'up' | 'down' | 'left' | 'right';
}

/** The complete, serializable world. */
export interface Save {
  readonly schemaVersion: number;
  readonly profile: PlayerProfile;
  /** The active team (ordered, 1–6). */
  readonly team: readonly PokemonInstance[];
  /** Box storage (overflow + collection). */
  readonly boxes: readonly {
    readonly id: string;
    readonly name: string;
    readonly contents: readonly PokemonInstance[];
  }[];
  readonly dex: DexState;
  readonly ranch: RanchState;
  readonly society: SocietyState;
  readonly lore: LoreState;
  /** Quest/story progress (D17). Progress only — authored defs live in data/quests/. */
  readonly quests: QuestState;
  /** Follower care (Round 3; optional — absent reads as empty). */
  readonly care?: CareState;
  /** Elite Four / Champion progress (Round 3; optional — absent reads as empty). */
  readonly league?: LeagueState;
  /** The pinned rival (Round 3; optional/null — resolved lazily on first meeting). */
  readonly rival?: RivalState | null;
  /** Walkable-overworld position (Round 3; optional/null — null ⇒ area default spawn). */
  readonly overworld?: OverworldPos | null;
  /** The world seed: anchors all deterministic generation (D9). */
  readonly worldSeed: number;
  /** Current in-game date (ISO-like string; anchors the daily cadence). */
  readonly inGameDate: string;
  /** Real wall-clock ms of last play (diffed on load for the overnight tick). */
  readonly lastPlayedAt: number;
  readonly settings: Settings;
}

/**
 * A migration upgrades a save one schema version forward. The store/ layer chains
 * these from a save's `schemaVersion` up to `SCHEMA_VERSION`. Input is `unknown`
 * because the old shape predates the current types.
 */
export type SaveMigration = (old: unknown) => Save;
